import { Box, Stack, FormLabel, CircularProgress } from '@mui/joy';
import { useFieldArray } from 'react-hook-form';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DndContext, useSensors, useSensor, PointerSensor, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HorizontallyScrollable } from '@/components/HorizontallyScrollable';
import { InputFileUpload } from '@/components/InputFileUpload';
import { ImageThumbnailCard } from '@/components/ImageThumbnailCard';
import { IMAGE_UPLOAD_ACCEPT } from '@/utils/images';
import { extractUploadErrorMessage } from '@/utils/errors';

export function FormImages({ name, label, useUploadImageMutation }) {
  const { fields, append, remove, move } = useFieldArray({ name });
  const { householdId } = useParams();
  const [uploadImage, { isLoading: isUploading }] = useUploadImageMutation();
  const [uploadError, setUploadError] = useState('');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 }}));

  async function handleFileUpload(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length || !householdId) return;

    setUploadError('');
    try {
      for (const file of files) {
        const result = await uploadImage({ householdId, file }).unwrap();
        append({ ...result.data, url: URL.createObjectURL(file) });
      }
    } catch (error) {
      setUploadError(extractUploadErrorMessage(error));
    }
  }

  function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex(field => field.id === active.id);
    const newIndex = fields.findIndex(field => field.id === over.id);
    move(oldIndex, newIndex);
  }

  return (
    <Box>
      <FormLabel sx={{ mb: 0.75}}>{label}</FormLabel>
      {fields.length > 0 && <>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Box sx={{ mb: 1 }}>
            <HorizontallyScrollable>
              <SortableContext items={fields} strategy={horizontalListSortingStrategy}>
                {fields.map((field, index) => <SortableItem key={field.id} field={field} index={index} onRemove={remove} />)}
              </SortableContext>
            </HorizontallyScrollable>
          </Box>
        </DndContext>
      </>}
      <Stack direction="row" alignItems="center" gap={1}>
        <InputFileUpload
          title={isUploading ? 'Uploading...' : 'Upload Image'}
          onChange={handleFileUpload}
          accept={IMAGE_UPLOAD_ACCEPT}
          multiple
        />
        {isUploading && <CircularProgress size="sm" />}
      </Stack>
      {uploadError && (
        <Box sx={{ mt: 1, color: 'danger.500', fontSize: 'sm' }}>{uploadError}</Box>
      )}
    </Box>
  );
}

function SortableItem({ field, index, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({id: field.id});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ImageThumbnailCard url={field.url} alt={`Image ${index + 1}`} onRemove={() => onRemove(index)} sx={{ flexShrink: 0 }} />
    </div>
  );
}
